import type { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import type {
  LinkedIcs213rr,
  LinkedIcs213rrItem,
} from 'components/IAP/hooks/resourceSetHooks';

export function linkedItemsToRequestItems(
  items: LinkedIcs213rrItem[] | undefined
): ResourceRequestItem[] {
  return (items ?? []).map((item, index) => ({
    id: `linked-${index}`,
    name: item.name ?? item.description ?? '',
    description: item.description ?? item.name ?? '',
    kind: item.kind ?? '',
    type: item.type ?? '',
    unit: item.unit || 'unit',
    quantity: Number(item.quantity) || 0,
    priority: item.priority ?? undefined,
    resource_set_id: item.resource_set_id ?? undefined,
    owning_organization: item.owning_organization ?? undefined,
    requested_reporting_location:
      item.requested_reporting_location ?? undefined,
    requested_reporting_location_id:
      item.requested_reporting_location_id ?? undefined,
    requested_reporting_location_geolocation:
      item.requested_reporting_location_geolocation ?? null,
    cost: finiteCost(item.cost) ?? undefined,
    attached_to_asset: item.attached_to_asset || undefined,
    status: 'pending',
  }));
}

function finiteCost(cost: unknown): number | null {
  if (cost === null || cost === undefined || cost === '') return null;
  const value = Number(cost);
  return Number.isFinite(value) ? value : null;
}

export type LinkedIcs213rrAsset = {
  name?: string | null;
  kind?: string | null;
  type?: string | null;
};

/** Every Linked ICS 213RR starts with a row whose Attach is the owning asset. */
export function withDefaultAssetRow(
  items: ResourceRequestItem[],
  asset: LinkedIcs213rrAsset
): ResourceRequestItem[] {
  if (items.some((item) => item.attached_to_asset)) return items;
  const name = asset.name?.trim() ?? '';
  return [
    {
      id: 'linked-asset',
      name,
      description: name,
      kind: asset.kind ?? '',
      type: asset.type ?? '',
      unit: 'unit',
      quantity: 1,
      status: 'pending',
      attached_to_asset: true,
    },
    ...items,
  ];
}

export function requestItemsToLinkedItems(
  items: ResourceRequestItem[]
): LinkedIcs213rrItem[] {
  return items.map((item) => ({
    name: item.name || null,
    description: item.description || item.name || null,
    kind: item.kind || null,
    type: item.type || null,
    unit: item.unit || 'unit',
    quantity: Number(item.quantity) || null,
    priority: item.priority || null,
    resource_set_id: item.resource_set_id || null,
    owning_organization: item.owning_organization || null,
    requested_reporting_location: item.requested_reporting_location || null,
    requested_reporting_location_id:
      item.requested_reporting_location_id || null,
    requested_reporting_location_geolocation:
      item.requested_reporting_location_geolocation ?? null,
    cost: finiteCost(item.cost),
    attached_to_asset: item.attached_to_asset || null,
  }));
}

/**
 * The catalog asset a task row points at, when it has a saved Linked ICS 213RR.
 * Task rows may store either the stable `resource_set_id` or a version row
 * `id`.
 */
export function findAssetWithLinkedIcs213rr<
  T extends {
    id: string;
    resource_set_id: string;
    linked_ics213rr?: LinkedIcs213rr | null;
  },
>(
  resourceSets: T[],
  resourceSetId: string | null | undefined
): (T & { linked_ics213rr: LinkedIcs213rr }) | null {
  if (!resourceSetId) return null;
  const asset = resourceSets.find(
    (resource) =>
      resource.id === resourceSetId ||
      resource.resource_set_id === resourceSetId
  );
  if (!asset || !hasLinkedIcs213rr(asset.linked_ics213rr)) return null;
  return asset as T & { linked_ics213rr: LinkedIcs213rr };
}

export function hasLinkedIcs213rr(
  value: LinkedIcs213rr | null | undefined
): value is LinkedIcs213rr {
  if (!value) return false;
  return Boolean(
    value.name?.trim() ||
      (value.items?.length ?? 0) > 0 ||
      value.supply_sources?.trim() ||
      value.substitutes?.trim()
  );
}
